import * as React from "react";
import { useState } from "react";
import { ArrowUpCircle, TrendingDown, CheckCircle } from "lucide-react";
import { useGetAllQuery } from "../store/studentApi";
import { useSelector } from "react-redux";
import { RootState } from "@/types";

import { useGetAllFeeQuery } from "@/store/feeApi";
const FeeSummaryCard = ({ pendingFees = 0, receivedFees = 0 }) => {
  const [animatedPending, setAnimatedPending] = useState(0);
  const [animatedReceived, setAnimatedReceived] = useState(0);

  const { user } = useSelector((state: RootState) => state.user);
  const { data: students } = useGetAllQuery(user?.token);
  const { data: Fee } = useGetAllFeeQuery(user?.token);
  // Calculate total amount
  const Drop = students?.filter((stu) => stu.status === "Disable");

  // // Calculate payable amount for active students
  // const DropStudentPending = Drop?.reduce((total, student) => {
  //   const studentFee = Fee?.find((fee) => fee.student === student._id);
  //   return total + (studentFee?.payable - studentFee?.paid || 0);
  // }, 0);

  const totalAmount =
    pendingFees === 0 && receivedFees === 0 ? 0 : pendingFees + receivedFees;

  React.useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const stepTime = duration / steps;

    setAnimatedPending(0);
    setAnimatedReceived(0);

    let currentPending = 0;
    let currentReceived = 0;
    const pendingIncrement = pendingFees / steps;
    const receivedIncrement = receivedFees / steps;

    if (pendingFees === 0 && receivedFees === 0) {
      return;
    }

    const timer = setInterval(() => {
      let shouldClearInterval = true;

      if (pendingFees > 0 && currentPending < pendingFees) {
        currentPending += pendingIncrement;
        setAnimatedPending(Math.min(currentPending, pendingFees));
        shouldClearInterval = false;
      } else if (pendingFees === 0) {
        setAnimatedPending(0);
      }

      if (receivedFees > 0 && currentReceived < receivedFees) {
        currentReceived += receivedIncrement;
        setAnimatedReceived(Math.min(currentReceived, receivedFees));
        shouldClearInterval = false;
      }

      if (shouldClearInterval) {
        clearInterval(timer);
      }
    }, stepTime);

    return () => clearInterval(timer);
  }, [pendingFees, receivedFees, totalAmount]);

  // Format currency function
  const formatCurrency = (amount) => {
    return `₹${Math.round(amount).toLocaleString("en-IN")}`;
  };

  // Calculate percentage for received and pending
  const receivedPercentage =
    totalAmount > 0 ? Math.round((receivedFees / totalAmount) * 100) : 0;
  const pendingPercentage =
    totalAmount > 0 ? Math.round((pendingFees / totalAmount) * 100) : 0;

  return (
    <div className="w-full bg-background rounded-lg shadow-sm p-4">
      <div className="flex flex-wrap md:flex-nowrap gap-4 items-stretch justify-between">
        {/* {/ {/ Total Amount /} /} */}
        <div className="flex-1 min-w-[200px] bg-blue-50 dark:bg-card rounded-lg p-4 border-l-4 border-blue-500">
          <div className="flex items-center gap-2 mb-1">
            <ArrowUpCircle className="w-5 h-5 text-blue-600" />
            <h3 className="text-sm font-medium text-blue-900">Total Payable</h3>
          </div>
          <p className="text-2xl font-bold text-blue-700">
            {formatCurrency(totalAmount)}
          </p>
          <div className="mt-1 text-xs text-blue-600">
            Total fees to be collected
          </div>
        </div>

        {/* {/ {/ Received Amount /} /} */}
        <div className="flex-1 min-w-[200px] bg-red-50 dark:bg-card rounded-lg p-4 border-l-4 border-red-500">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-5 h-5 text-red-600" />
            <h3 className="text-sm font-medium text-red-900">Received</h3>
          </div>
          <p className="text-2xl font-bold text-red-700">
            {formatCurrency(animatedReceived)}
          </p>
          <div className="mt-1 text-xs text-red-600">
            {receivedPercentage}% collected
          </div>
        </div>

        {/* {/ {/ Pending Amount /} /} */}
        <div className="flex-1 min-w-[200px] bg-red-50 dark:bg-card rounded-lg p-4 border-l-4 border-red-500">
          <div className="flex items-center gap-2 mb-1">
            <TrendingDown className="w-5 h-5 text-red-600" />
            <h3 className="text-sm font-medium text-red-900">Pending</h3>
          </div>
          <p className="text-2xl font-bold text-red-700">
            {formatCurrency(animatedPending)}
          </p>
          <div className="mt-1 text-xs text-red-600">
            {pendingPercentage}% remaining
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeeSummaryCard;
